import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const PaymentDetailsForm = ({ 
  paymentData, 
  onPaymentDataChange, 
  selectedMethod,
  errors = {},
  disabled = false 
}) => {
  const [localErrors, setLocalErrors] = useState({});

  const venezuelanBanks = [
    { value: 'banesco', label: 'Banesco' },
    { value: 'mercantil', label: 'Banco Mercantil' },
    { value: 'venezuela', label: 'Banco de Venezuela' },
    { value: 'provincial', label: 'BBVA Provincial' },
    { value: 'bicentenario', label: 'Banco Bicentenario' },
    { value: 'exterior', label: 'Banco Exterior' },
    { value: 'caroni', label: 'Banco Caroní' },
    { value: 'fondo_comun', label: 'Banco Fondo Común' },
    { value: 'plaza', label: 'Banco Plaza' },
    { value: 'sofitasa', label: 'Banco Sofitasa' }
  ];

  const validateReference = (reference) => {
    const cleanRef = reference?.replace(/\D/g, '');
    if (cleanRef?.length !== 6) {
      return 'La referencia debe tener exactamente 6 dígitos';
    }
    return '';
  };

  const validateTransactionHash = (hash) => {
    if (selectedMethod === 'binance' && hash?.length < 10) {
      return 'Ingresa un hash de transacción válido';
    }
    return '';
  };

  const handleInputChange = (field, value) => {
    let formattedValue = value;
    let error = '';

    if (field === 'reference') {
      formattedValue = value?.replace(/\D/g, '')?.slice(0, 6);
      error = validateReference(formattedValue);
    } else if (field === 'transactionHash') {
      error = validateTransactionHash(value);
    }

    onPaymentDataChange({ ...paymentData, [field]: formattedValue });
    setLocalErrors(prev => ({ ...prev, [field]: error }));
  };

  const getDisplayError = (field) => {
    return errors?.[field] || localErrors?.[field] || '';
  };

  const getMethodSpecificFields = () => {
    switch (selectedMethod) {
      case 'pago_movil':
        return (
          <>
            <Select
              label="Banco Emisor"
              placeholder="Selecciona tu banco"
              options={venezuelanBanks}
              value={paymentData?.bankIssuer || ''}
              onChange={(value) => onPaymentDataChange({ ...paymentData, bankIssuer: value })}
              error={getDisplayError('bankIssuer')}
              required
              disabled={disabled}
              className="md:col-span-1"
            />
            <Input
              label="Número de Referencia"
              type="text"
              placeholder="123456"
              value={paymentData?.reference || ''}
              onChange={(e) => handleInputChange('reference', e?.target?.value)}
              error={getDisplayError('reference')}
              required
              disabled={disabled}
              description="6 dígitos del comprobante"
              className="md:col-span-1"
            />
          </>
        );

      case 'zelle':
        return (
          <Input
            label="Número de Confirmación Zelle"
            type="text"
            placeholder="Número de confirmación"
            value={paymentData?.zelleConfirmation || ''}
            onChange={(e) => onPaymentDataChange({ ...paymentData, zelleConfirmation: e?.target?.value })}
            error={getDisplayError('zelleConfirmation')}
            required
            disabled={disabled}
            description="Número que aparece en tu confirmación de Zelle"
            className="md:col-span-2"
          />
        );

      case 'binance':
        return (
          <>
            <Input
              label="Hash de Transacción"
              type="text"
              placeholder="0x1234567890abcdef..."
              value={paymentData?.transactionHash || ''}
              onChange={(e) => handleInputChange('transactionHash', e?.target?.value)}
              error={getDisplayError('transactionHash')}
              required
              disabled={disabled}
              description="Hash de la transacción en blockchain"
              className="md:col-span-2"
            />
            <Select
              label="Red de Blockchain"
              placeholder="Selecciona la red"
              options={[
                { value: 'trc20', label: 'TRC20 (USDT)' },
                { value: 'erc20', label: 'ERC20 (USDT)' },
                { value: 'bep20', label: 'BEP20 (USDT)' },
                { value: 'bitcoin', label: 'Bitcoin (BTC)' }
              ]}
              value={paymentData?.network || ''}
              onChange={(value) => onPaymentDataChange({ ...paymentData, network: value })}
              error={getDisplayError('network')}
              required
              disabled={disabled}
              className="md:col-span-1"
            />
            <Input
              label="Monto Enviado"
              type="number"
              placeholder="50.00"
              value={paymentData?.cryptoAmount || ''}
              onChange={(e) => onPaymentDataChange({ ...paymentData, cryptoAmount: e?.target?.value })}
              error={getDisplayError('cryptoAmount')}
              required
              disabled={disabled}
              description="Monto en USD/USDT/BTC"
              className="md:col-span-1"
            />
          </>
        );

      default:
        return null;
    }
  };

  if (!selectedMethod) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 shadow-luxury">
        <div className="text-center py-8">
          <Icon name="CreditCard" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-heading font-semibold text-gray-800 mb-2"> {/* Cambiado aquí */}
            Selecciona un Método de Pago
          </h3>
          <p className="text-sm text-gray-800 font-caption"> {/* Cambiado aquí */}
            Elige tu método de pago preferido para continuar
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-6 shadow-luxury">
      <div className="flex items-center space-x-2 mb-6">
        <div className="p-2 bg-secondary/10 rounded-lg">
          <Icon name="FileText" size={20} className="text-secondary" />
        </div>
        <div>
          <h3 className="text-lg font-heading font-semibold text-gray-800"> {/* Cambiado aquí */}
            Detalles del Pago
          </h3>
          <p className="text-sm text-gray-800 font-caption"> {/* Cambiado aquí */}
            Completa la información de tu transferencia
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {getMethodSpecificFields()}

        <Input
          label="Monto Transferido (Bs.)"
          type="number"
          placeholder="1825.00"
          value={paymentData?.amount || ''}
          onChange={(e) => onPaymentDataChange({ ...paymentData, amount: e?.target?.value })}
          error={getDisplayError('amount')}
          required
          disabled={disabled}
          description="Monto exacto que transferiste"
          className="md:col-span-1"
        />

        <Input
          label="Fecha de Transferencia"
          type="date"
          value={paymentData?.transferDate || new Date()?.toISOString()?.split('T')?.[0]}
          onChange={(e) => onPaymentDataChange({ ...paymentData, transferDate: e?.target?.value })}
          error={getDisplayError('transferDate')}
          required
          disabled={disabled}
          className="md:col-span-1"
        />
      </div>
      <div className="mt-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
          <div>
            <h4 className="font-body font-medium text-gray-800 text-sm"> {/* Cambiado aquí */}
              Información Importante
            </h4>
            <p className="text-xs text-gray-800 font-caption mt-1"> {/* Cambiado aquí */}
              Asegúrate de que todos los datos coincidan exactamente con tu comprobante de pago. 
              Cualquier discrepancia puede retrasar la verificación de tu compra.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailsForm;